import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, XCircle, AlertTriangle } from 'lucide-react';

const PauseInputRow = ({ 
  value, 
  onChange, 
  onRemove, 
  error,
  warning,
  showRemove = true 
}) => {
  const presetOptions = useMemo(() => [
    { value: '15', label: '15 Minuten', duration: 15 },
    { value: '30', label: '30 Minuten', duration: 30 },
    { value: '45', label: '45 Minuten', duration: 45 },
    { value: '60', label: '60 Minuten', duration: 60 },
    { value: 'custom', label: 'Custom...' }
  ], []);

  // Initialize state based on value
  const getInitialState = useMemo(() => {
    if (value && typeof value === 'number') {
      const preset = presetOptions.find(opt => opt.duration === value);
      if (preset) {
        return {
          inputMode: 'dropdown',
          dropdownValue: preset.value,
          customValue: ''
        };
      } else {
        const hours = Math.floor(value / 60);
        const minutes = value % 60;
        return {
          inputMode: 'custom',
          dropdownValue: 'custom',
          customValue: `${hours}:${minutes.toString().padStart(2, '0')}`
        };
      }
    }
    return {
      inputMode: 'dropdown',
      dropdownValue: '',
      customValue: ''
    };
  }, [value, presetOptions]);

  const [inputMode, setInputMode] = useState(getInitialState.inputMode);
  const [customValue, setCustomValue] = useState(getInitialState.customValue);
  const [dropdownValue, setDropdownValue] = useState(getInitialState.dropdownValue);
  
  const validateCustomInput = (input) => {
    if (!input.trim()) {
      return { isValid: false, error: 'Bitte geben Sie eine Dauer ein' };
    }
    
    // Regex für h:mm Format (führende Null optional)
    const timePattern = /^(\d{1,2}:)?[0-5]?\d$/;
    
    if (!timePattern.test(input)) {
      return { 
        isValid: false, 
        error: 'Ungültiges Format. Verwenden Sie h:mm (z.B. 0:15, 1:30, 2:00)' 
      };
    }
    
    // Parse und Range-Check
    let hours = 0;
    let minutes = 0;
    
    if (input.includes(':')) {
      const parts = input.split(':');
      hours = parseInt(parts[0]) || 0;
      minutes = parseInt(parts[1]) || 0;
    } else {
      minutes = parseInt(input) || 0;
    }
    
    if (minutes > 59) {
      return { isValid: false, error: 'Minuten müssen zwischen 0 und 59 liegen' };
    }
    
    const totalMinutes = hours * 60 + minutes;
    
    if (totalMinutes < 1) {
      return { isValid: false, error: 'Pause muss mindestens 1 Minute betragen' };
    }
    
    if (totalMinutes > 480) { // 8 Stunden
      return { isValid: false, error: 'Pause über 8 Stunden nicht erlaubt' };
    }
    
    if (totalMinutes > 240) { // 4 Stunden
      return { 
        isValid: true, 
        warning: 'Warnung: Pause über 4 Stunden ist ungewöhnlich' 
      };
    }
    
    return { isValid: true };
  };
  
  const handleDropdownChange = (selectedValue) => {
    if (selectedValue === 'custom') {
      // Check if we have a valid custom value to preserve
      if (customValue && validateCustomInput(customValue).isValid) {
        // Keep existing custom value
        setInputMode('custom');
        setDropdownValue('custom');
      } else {
        setInputMode('custom');
        setDropdownValue('custom');
        setCustomValue('');
        onChange(null); // Clear value until custom input is filled
      }
    } else {
      const preset = presetOptions.find(opt => opt.value === selectedValue);
      setInputMode('dropdown');
      setDropdownValue(selectedValue);
      onChange(preset.duration);
    }
  };
  
  const handleCustomChange = (customInput) => {
    setCustomValue(customInput);
    const validation = validateCustomInput(customInput);
    
    if (validation.isValid) {
      // Parse the input to get total minutes
      let hours = 0;
      let minutes = 0;
      
      if (customInput.includes(':')) {
        const parts = customInput.split(':');
        hours = parseInt(parts[0]) || 0;
        minutes = parseInt(parts[1]) || 0;
      } else {
        minutes = parseInt(customInput) || 0;
      }
      
      const totalMinutes = hours * 60 + minutes;
      onChange(totalMinutes);
    } else {
      onChange(null); // Invalid input doesn't contribute to calculation
    }
  };
  
  const currentValidation = inputMode === 'custom' ? validateCustomInput(customValue) : { isValid: true };
  const displayError = error || (inputMode === 'custom' && !currentValidation.isValid ? currentValidation.error : null);
  const displayWarning = warning || (inputMode === 'custom' && currentValidation.warning ? currentValidation.warning : null);
  
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
        {/* Dropdown */}
        <div className="flex-1">
          <Select value={dropdownValue} onValueChange={handleDropdownChange}>
            <SelectTrigger className={`${
              displayError ? 'border-red-500' : 
              displayWarning ? 'border-yellow-500' : 
              'border-gray-300'
            }`}>
              <SelectValue placeholder="Pause auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {presetOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Custom Input (nur sichtbar wenn Custom gewählt) */}
        {inputMode === 'custom' && (
          <div className="flex-1">
            <Input
              value={customValue}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="z.B. 0:15, 1:30, 2:00"
              className={`${
                displayError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' :
                displayWarning ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500' :
                customValue && currentValidation.isValid ? 'border-green-500 focus:border-green-500 focus:ring-green-500' :
                'border-gray-300'
              }`}
            />
          </div>
        )}
        
        {/* Remove Button */}
        {showRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Error/Warning Message */}
      {(displayError || displayWarning) && (
        <div className={`text-sm flex items-center gap-1 ${
          displayError ? 'text-red-600' : 'text-yellow-600'
        }`}>
          {displayError ? (
            <XCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <span>{displayError || displayWarning}</span>
        </div>
      )}
    </div>
  );
};

export default PauseInputRow;